import { ArrowDown, MapPin, Mail, Phone, CalendarCheck, Clock, Video, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import heroBackground from "@/assets/hero-background-biochemistry.webp";
import profileImage from "@/assets/profile-isabella-hero-optimized.webp";
import { useT, useAnchorHref, useLocalePath } from "@/i18n";

const Hero = () => {
  const anchor = useAnchorHref();
  const lp = useLocalePath();
  const t = useT(
    {
      tagline: "Wo Wissenschaft auf Natur trifft",
      pillars: "Mikrobiom & Mikrobiologie · Biochemie & Mikronährstoffe · Phytotherapie · Korrelative Analysen",
      lead: "Chronische Beschwerden verstehen. Zusammenhänge erkennen. Gesundheit individuell begleiten.",
      emailCta: "E-Mail Anfrage",
      emailAria: "E-Mail an office@ibpraxis.net schreiben",
      bookCta: "Termin buchen",
      phoneAria: "Anrufen: +43 660 4041993",
      location: "Ärztezentrum West, 1140 Wien",
      scroll: "Weiter zum Inhalt",
      factPrice: "Erstgespräch 139 € · ca. 60 Minuten",
      factPlace: "Wien (Zoom n. Vereinbarung)",
      factRating: "5,0 von 5 aus 22 Google-Bewertungen",
    },
    {
      tagline: "Where science meets nature",
      pillars: "Microbiome & microbiology · Biochemistry & micronutrients · Phytotherapy · Correlative analyses",
      lead: "Understanding chronic complaints. Recognising connections. Individual guidance.",
      emailCta: "Email enquiry",
      emailAria: "Write an email to office@ibpraxis.net",
      bookCta: "Book an appointment",
      phoneAria: "Call: +43 660 4041993",
      location: "Ärztezentrum West medical centre, 1140 Vienna",
      scroll: "Scroll to content",
      factPrice: "First consultation €139 · approx. 60 minutes",
      factPlace: "Vienna (Zoom by arrangement)",
      factRating: "5.0 out of 5 from 22 Google reviews",
    },
  );


  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image (eager + high priority for LCP) */}
      <img
        src={heroBackground}
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: 'brightness(0.85) saturate(1)' }}
      />
      
      {/* Gradient overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-700/30 via-amber-700/20 to-teal-600/30 pointer-events-none" />

      {/* DNA Helix Elements - hidden on mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
        {[...Array(4)].map((_, i) => (
          <div
            key={`dna-${i}`}
            className="absolute animate-dna-float"
            style={{
              left: `${10 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
              animationDelay: `${i * 0.8}s`,
              opacity: 0.6
            }}
          >
            <svg width="40" height="80" viewBox="0 0 40 80" className="text-teal-300/50">
              <path 
                d="M10 0 Q25 10 10 20 Q-5 30 10 40 Q25 50 10 60 Q-5 70 10 80" 
                stroke="currentColor" 
                strokeWidth="2" 
                fill="none"
                className="animate-dna-pulse"
              />
              <path 
                d="M30 0 Q15 10 30 20 Q45 30 30 40 Q15 50 30 60 Q45 70 30 80" 
                stroke="currentColor" 
                strokeWidth="2" 
                fill="none"
                className="animate-dna-pulse"
                style={{ animationDelay: '0.5s' }}
              />
              {[10, 30, 50, 70].map((y) => (
                <line key={y} x1="12" y1={y} x2="28" y2={y} stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
              ))}
            </svg>
          </div>
        ))}
      </div>

      {/* Microbe/Cell Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div
            key={`microbe-${i}`}
            className="absolute rounded-full animate-microbe"
            style={{
              width: `${15 + (i % 4) * 10}px`,
              height: `${15 + (i % 4) * 10}px`,
              left: `${5 + i * 8}%`,
              top: `${10 + (i % 5) * 18}%`,
              background: `radial-gradient(circle at 30% 30%, rgba(45, 212, 191, ${0.3 + (i % 3) * 0.1}) 0%, rgba(20, 184, 166, 0.1) 60%, transparent 70%)`,
              border: `1px solid rgba(94, 234, 212, ${0.2 + (i % 2) * 0.1})`,
              boxShadow: `0 0 ${10 + i * 2}px rgba(45, 212, 191, 0.2)`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${8 + (i % 4) * 3}s`,
            }}
          >
            <div 
              className="absolute rounded-full"
              style={{
                width: '40%',
                height: '40%',
                top: '20%',
                left: '20%',
                background: 'radial-gradient(circle, rgba(94, 234, 212, 0.4) 0%, transparent 70%)'
              }}
            />
          </div>
        ))}
      </div>

      {/* Molecule/Atom Elements - hidden on mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
        {[...Array(5)].map((_, i) => (
          <div
            key={`molecule-${i}`}
            className="absolute animate-molecule"
            style={{
              left: `${8 + i * 12}%`,
              bottom: `${15 + (i % 4) * 20}%`,
              animationDelay: `${i * 0.7}s`,
            }}
          >
            <svg width="50" height="50" viewBox="0 0 50 50" className="text-amber-300/40">
              <circle cx="25" cy="25" r="6" fill="currentColor" opacity="0.6" />
              <circle cx="25" cy="25" r="15" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3" />
              <circle cx="10" cy="25" r="3" fill="currentColor" className="animate-electron" />
              <circle cx="40" cy="25" r="3" fill="currentColor" className="animate-electron" style={{ animationDelay: '0.5s' }} />
              <circle cx="25" cy="10" r="3" fill="currentColor" className="animate-electron" style={{ animationDelay: '1s' }} />
            </svg>
          </div>
        ))}
      </div>

      {/* Growing Nature Elements - Leaves/Vines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Left side nature growth */}
        <div className="absolute left-0 bottom-0 w-1/3 h-2/3">
          {[...Array(5)].map((_, i) => (
            <div
              key={`leaf-left-${i}`}
              className="absolute animate-grow-leaf"
              style={{
                left: `${10 + i * 15}%`,
                bottom: `${5 + i * 12}%`,
                animationDelay: `${i * 0.4}s`,
                transform: `rotate(${-20 + i * 10}deg)`,
              }}
            >
              <svg width="40" height="60" viewBox="0 0 40 60" className="text-emerald-500/50">
                <path 
                  d="M20 60 Q20 30 5 15 Q20 20 35 15 Q20 30 20 60" 
                  fill="currentColor"
                />
                <path 
                  d="M20 55 L20 20" 
                  stroke="rgba(16, 185, 129, 0.6)" 
                  strokeWidth="1.5" 
                  fill="none"
                />
              </svg>
            </div>
          ))}
        </div>
        
        {/* Right side nature growth */}
        <div className="absolute right-0 bottom-0 w-1/3 h-2/3">
          {[...Array(5)].map((_, i) => (
            <div
              key={`leaf-right-${i}`}
              className="absolute animate-grow-leaf"
              style={{
                right: `${10 + i * 15}%`,
                bottom: `${5 + i * 12}%`,
                animationDelay: `${0.2 + i * 0.4}s`,
                transform: `rotate(${20 - i * 10}deg) scaleX(-1)`,
              }}
            >
              <svg width="40" height="60" viewBox="0 0 40 60" className="text-emerald-500/50">
                <path 
                  d="M20 60 Q20 30 5 15 Q20 20 35 15 Q20 30 20 60" 
                  fill="currentColor"
                />
                <path 
                  d="M20 55 L20 20" 
                  stroke="rgba(16, 185, 129, 0.6)" 
                  strokeWidth="1.5" 
                  fill="none"
                />
              </svg>
            </div>
          ))}
        </div>
      </div>

      {/* Sunshine Rays Layer - reduced; heavy blurs hidden on mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/4 w-[250%] h-48 bg-gradient-to-r from-transparent via-yellow-300/60 to-transparent rotate-[-35deg] blur-md animate-sunray-1" />
        <div className="absolute -top-1/2 -right-1/4 w-[220%] h-44 bg-gradient-to-r from-transparent via-yellow-400/65 to-transparent rotate-[-40deg] blur-lg animate-sunray-4 hidden md:block" />
        <div className="absolute -top-1/2 left-1/3 w-[180%] h-56 bg-gradient-to-r from-transparent via-yellow-300/70 to-transparent rotate-[-30deg] blur-2xl animate-sunray-7 hidden md:block" />
      </div>

      {/* Sun Glow Source */}
      <div 
        className="absolute -top-20 left-1/4 w-80 h-80 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(255,220,100,0.8) 0%, rgba(255,180,50,0.4) 30%, rgba(255,150,0,0.1) 60%, transparent 80%)',
          filter: 'blur(20px)',
          boxShadow: '0 0 120px 60px rgba(255,200,100,0.4)'
        }}
      />

      {/* Floating Light Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Small golden fireflies - reduced for performance */}
        {[...Array(6)].map((_, i) => (
          <div
            key={`firefly-${i}`}
            className="absolute rounded-full animate-firefly"
            style={{
              width: `${4 + (i % 4) * 2}px`,
              height: `${4 + (i % 4) * 2}px`,
              left: `${5 + (i * 8)}%`,
              bottom: `${-5 - (i % 6) * 3}%`,
              background: 'radial-gradient(circle, rgba(255,220,50,1) 0%, rgba(255,190,0,0.8) 40%, transparent 70%)',
              boxShadow: `0 0 ${8 + (i % 4) * 5}px rgba(255,210,50,0.9), 0 0 ${16 + (i % 5) * 8}px rgba(255,180,0,0.5)`,
              animationDelay: `${i * 0.8}s`,
              animationDuration: `${12 + (i % 5) * 3}s`,
            }}
          />
        ))}
        {/* Medium glowing particles - hidden on mobile */}
        {[...Array(8)].map((_, i) => (
          <div
            key={`glow-${i}`}
            className="absolute rounded-full animate-firefly-slow hidden md:block"
            style={{
              width: `${5 + (i % 3) * 3}px`,
              height: `${5 + (i % 3) * 3}px`,
              left: `${8 + (i * 11)}%`,
              bottom: `${-8 - (i % 5) * 4}%`,
              background: 'radial-gradient(circle, rgba(255,240,180,1) 0%, rgba(255,210,120,0.8) 30%, transparent 60%)',
              boxShadow: `0 0 12px rgba(255,230,120,1), 0 0 24px rgba(255,200,80,0.7)`,
              animationDelay: `${0.5 + i * 1}s`,
              animationDuration: `${18 + (i % 4) * 4}s`,
            }}
          />
        ))}
        {/* Tiny sparkles - reduced and hidden on mobile */}
        {[...Array(8)].map((_, i) => (
          <div
            key={`sparkle-${i}`}
            className="absolute rounded-full animate-sparkle hidden sm:block"
            style={{
              width: `${2 + (i % 2)}px`,
              height: `${2 + (i % 2)}px`,
              left: `${3 + (i * 6.5)}%`,
              bottom: `${-3 - (i % 7) * 2}%`,
              background: 'rgba(255,255,220,1)',
              boxShadow: '0 0 6px rgba(255,240,180,1), 0 0 12px rgba(255,220,120,0.8)',
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${10 + (i % 6) * 2}s`,
            }}
          />
        ))}
        {/* Large golden orbs - reduced */}
        {[...Array(6)].map((_, i) => (
          <div
            key={`orb-${i}`}
            className="absolute rounded-full animate-orb"
            style={{
              width: `${8 + (i % 3) * 4}px`,
              height: `${8 + (i % 3) * 4}px`,
              left: `${10 + (i * 15)}%`,
              bottom: `${-10 - (i % 4) * 5}%`,
              background: 'radial-gradient(circle, rgba(255,215,0,1) 0%, rgba(255,180,0,0.6) 50%, transparent 70%)',
              boxShadow: `0 0 20px rgba(255,200,50,1), 0 0 40px rgba(255,180,0,0.6)`,
              animationDelay: `${i * 1.2}s`,
              animationDuration: `${22 + (i % 3) * 4}s`,
            }}
          />
        ))}
        {/* Floating dust particles - hidden on mobile */}
        {[...Array(10)].map((_, i) => (
          <div
            key={`dust-${i}`}
            className="absolute rounded-full animate-dust hidden md:block"
            style={{
              width: '1.5px',
              height: '1.5px',
              left: `${5 + (i * 9.5)}%`,
              bottom: `${-2 - (i % 8) * 1.5}%`,
              background: 'rgba(255,250,200,0.9)',
              boxShadow: '0 0 3px rgba(255,230,150,1)',
              animationDelay: `${i * 0.4}s`,
              animationDuration: `${16 + (i % 4) * 4}s`,
            }}
          />
        ))}
      </div>

      {/* Warm Glow Overlay */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 30% 30%, rgba(251, 191, 36, 0.3) 0%, transparent 50%)'
        }}
      />

      {/* Main Content */}
      <div className="container mx-auto px-4 pt-20 relative z-10">
        <div className="max-w-5xl mx-auto flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          
          {/* Profile Image - Left Side */}
          <div className="flex-shrink-0">
            <img 
              src={profileImage} 
              alt="Isabella Bartak" 
              width={512}
              height={512}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="w-36 h-36 md:w-48 md:h-48 lg:w-64 lg:h-64 rounded-full bg-card object-contain ring-4 ring-white/20 shadow-2xl"
            />
          </div>

          {/* Text Content - Right Side */}
          <div className="text-center lg:text-left">

            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight animate-fade-in-up" 
              style={{ animationDelay: "0.1s", textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}
            >
              PRAXIS IB
              <span className="block text-2xl md:text-3xl lg:text-4xl font-normal mt-3 opacity-90">
                {t.tagline}
              </span>
            </h1>

            <p
              className="mt-4 text-sm md:text-base font-medium text-white/95 max-w-2xl mx-auto lg:mx-0 animate-fade-in-up"
              style={{ animationDelay: "0.15s", textShadow: '0 1px 12px rgba(0,0,0,0.4)' }}
            >
              {t.pillars}
            </p>

            <p
              className="mt-4 text-lg md:text-xl font-medium text-white max-w-xl mx-auto lg:mx-0 animate-fade-in-up"
              style={{ animationDelay: "0.2s", textShadow: '0 1px 12px rgba(0,0,0,0.35)' }}
            >
              {t.lead}
            </p>

            <ul
              className="mt-6 flex flex-wrap justify-center lg:justify-start gap-x-5 gap-y-2 text-sm text-white/90 animate-fade-in-up"
              style={{ animationDelay: "0.4s" }}
            >
              <li className="flex items-center gap-2"><Clock className="w-4 h-4 shrink-0" />{t.factPrice}</li>
              <li className="flex items-center gap-2"><Video className="w-4 h-4 shrink-0" />{t.factPlace}</li>
              <li className="flex items-center gap-2"><Star className="w-4 h-4 shrink-0 fill-current" />{t.factRating}</li>
            </ul>

            <div
              className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center lg:justify-start gap-3 animate-fade-in-up"
              style={{ animationDelay: "0.45s" }}
            >
              <Button asChild size="lg" className="gap-2 shadow-lg min-h-12">
                <Link to={lp("/termin")}>
                  <CalendarCheck className="w-5 h-5" />
                  {t.bookCta}
                </Link>
              </Button>
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-sm text-white/90">
                <a
                  href="mailto:office@ibpraxis.net"
                  aria-label={t.emailAria}
                  className="inline-flex items-center gap-2 py-2 underline underline-offset-4 hover:text-white"
                >
                  <Mail className="w-4 h-4" />
                  {t.emailCta}
                </a>
                <a
                  href="tel:+436604041993"
                  aria-label={t.phoneAria}
                  className="inline-flex items-center gap-2 py-2 underline underline-offset-4 hover:text-white"
                >
                  <Phone className="w-4 h-4" />
                  +43 660 4041993
                </a>
              </div>
            </div>

          </div>

        </div>

        <a 
          href={anchor("#willkommen")}
          aria-label={t.scroll}
          className="flex items-center justify-center mt-12 text-white/70 hover:text-white transition-colors animate-bounce"
        >
          <ArrowDown className="w-6 h-6" />
        </a>
      </div>

      {/* Location Card */}
      <div className="absolute bottom-4 right-4 md:bottom-8 md:right-8 z-20 bg-white/90 backdrop-blur-sm rounded-lg shadow-xl px-4 py-3 flex items-center gap-3 max-w-xs border border-primary/20">
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
          <MapPin className="w-5 h-5 text-primary" />
        </div>
        <div className="text-xs md:text-sm leading-tight">
          <p className="font-semibold text-foreground">PRAXIS IB</p>
          <p className="text-muted-foreground">{t.location}</p>
        </div>
      </div>

      <style>{`
        @keyframes sunray-1 {
          0%, 100% { transform: rotate(-35deg) translateX(-30px); opacity: 0.4; }
          50% { transform: rotate(-35deg) translateX(30px); opacity: 0.7; }
        }
        @keyframes sunray-2 {
          0%, 100% { transform: rotate(-25deg) translateX(25px); opacity: 0.35; }
          50% { transform: rotate(-25deg) translateX(-25px); opacity: 0.65; }
        }
        @keyframes sunray-3 {
          0%, 100% { transform: rotate(-45deg) translateX(-15px); opacity: 0.4; }
          50% { transform: rotate(-45deg) translateX(20px); opacity: 0.7; }
        }
        @keyframes sunray-4 {
          0%, 100% { transform: rotate(-40deg) translateX(15px); opacity: 0.4; }
          50% { transform: rotate(-40deg) translateX(-20px); opacity: 0.7; }
        }
        @keyframes sunray-5 {
          0%, 100% { transform: rotate(-15deg) translateX(-20px); opacity: 0.35; }
          50% { transform: rotate(-15deg) translateX(25px); opacity: 0.6; }
        }
        @keyframes sunray-6 {
          0%, 100% { transform: rotate(-55deg) translateX(10px); opacity: 0.35; }
          50% { transform: rotate(-55deg) translateX(-18px); opacity: 0.6; }
        }
        @keyframes sunray-7 {
          0%, 100% { transform: rotate(-30deg) translateX(-12px) scale(1); opacity: 0.4; }
          50% { transform: rotate(-30deg) translateX(15px) scale(1.05); opacity: 0.7; }
        }
        @keyframes firefly {
          0% { transform: translateY(0) translateX(0) scale(1); opacity: 0; }
          15% { opacity: 0.7; }
          35% { transform: translateY(-25vh) translateX(12px) scale(1.15); opacity: 0.8; }
          55% { transform: translateY(-45vh) translateX(-8px) scale(0.95); opacity: 0.6; }
          75% { transform: translateY(-65vh) translateX(15px) scale(1.1); opacity: 0.7; }
          90% { opacity: 0.4; }
          100% { transform: translateY(-100vh) translateX(6px) scale(1); opacity: 0; }
        }
        @keyframes firefly-slow {
          0% { transform: translateY(0) translateX(0) scale(1); opacity: 0; }
          20% { opacity: 0.6; transform: translateY(-12vh) translateX(-15px) scale(1.2); }
          45% { transform: translateY(-35vh) translateX(18px) scale(1); opacity: 0.7; }
          70% { transform: translateY(-55vh) translateX(-12px) scale(1.1); opacity: 0.6; }
          88% { opacity: 0.4; }
          100% { transform: translateY(-100vh) translateX(10px) scale(1.05); opacity: 0; }
        }
        @keyframes sparkle {
          0% { transform: translateY(0) scale(0.7); opacity: 0; }
          25% { opacity: 0.8; transform: translateY(-18vh) scale(1.1); }
          50% { opacity: 0.5; transform: translateY(-35vh) scale(0.9); }
          75% { opacity: 0.7; transform: translateY(-55vh) scale(1.15); }
          90% { opacity: 0.5; transform: translateY(-70vh) scale(1); }
          100% { transform: translateY(-90vh) scale(0.7); opacity: 0; }
        }
        @keyframes orb {
          0% { transform: translateY(0) translateX(0) scale(0.9); opacity: 0; }
          25% { opacity: 0.7; transform: translateY(-15vh) translateX(-18px) scale(1.1); }
          55% { transform: translateY(-40vh) translateX(25px) scale(1); opacity: 0.6; }
          85% { transform: translateY(-65vh) translateX(-12px) scale(1.15); opacity: 0.7; }
          100% { transform: translateY(-95vh) translateX(8px) scale(0.95); opacity: 0; }
        }
        @keyframes dust {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          30% { opacity: 0.6; transform: translateY(-22vh) translateX(6px); }
          55% { transform: translateY(-42vh) translateX(-5px); opacity: 0.4; }
          80% { transform: translateY(-65vh) translateX(8px); opacity: 0.5; }
          100% { transform: translateY(-90vh) translateX(-3px); opacity: 0; }
        }
        @keyframes dna-float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.25; }
          50% { transform: translateY(-12px) rotate(5deg); opacity: 0.45; }
        }
        @keyframes dna-pulse {
          0%, 100% { stroke-opacity: 0.3; }
          50% { stroke-opacity: 0.55; }
        }
        @keyframes microbe {
          0% { transform: translate(0, 0) scale(1); }
          30% { transform: translate(8px, -6px) scale(1.02); }
          55% { transform: translate(-5px, -12px) scale(0.98); }
          80% { transform: translate(10px, -8px) scale(1.04); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes molecule {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.35; }
          50% { transform: translate(-8px, 12px) rotate(90deg); opacity: 0.55; }
        }
        @keyframes electron {
          0% { transform: rotate(0deg) translateX(12px) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(12px) rotate(-360deg); }
        }
        @keyframes grow-leaf {
          0% { transform: scale(0) translateY(15px); opacity: 0; }
          70% { transform: scale(1.05) translateY(-3px); opacity: 0.45; }
          100% { transform: scale(1) translateY(0); opacity: 0.4; }
        }
        .animate-sunray-1 { animation: sunray-1 8s ease-in-out infinite; }
        .animate-sunray-2 { animation: sunray-2 10s ease-in-out infinite; }
        .animate-sunray-3 { animation: sunray-3 7s ease-in-out infinite; }
        .animate-sunray-4 { animation: sunray-4 9s ease-in-out infinite; }
        .animate-sunray-5 { animation: sunray-5 12s ease-in-out infinite; }
        .animate-sunray-6 { animation: sunray-6 8s ease-in-out infinite; }
        .animate-sunray-7 { animation: sunray-7 11s ease-in-out infinite; }
        .animate-firefly { animation: firefly 12s ease-in-out infinite; }
        .animate-firefly-slow { animation: firefly-slow 18s ease-in-out infinite; }
        .animate-sparkle { animation: sparkle 10s ease-in-out infinite; }
        .animate-orb { animation: orb 22s ease-in-out infinite; }
        .animate-dust { animation: dust 16s ease-in-out infinite; }
        .animate-dna-float { animation: dna-float 14s ease-in-out infinite; }
        .animate-dna-pulse { animation: dna-pulse 5s ease-in-out infinite; }
        .animate-microbe { animation: microbe 20s ease-in-out infinite; }
        .animate-molecule { animation: molecule 18s ease-in-out infinite; }
        .animate-electron { animation: electron 8s linear infinite; transform-origin: 25px 25px; }
        .animate-grow-leaf { animation: grow-leaf 3.5s ease-out forwards; }
      `}</style>
    </section>
  );
};

export default Hero;
